import { observable, action, computed, toJS } from 'mobx';
import { persist } from 'mobx-persist';
import { RouterStore } from 'mobx-react-router';
import {
  GENDER_TYPES,
  SizeTypes,
  PUBLIC_SAVE_URL,
  PUBLIC_LOGIN_URL,
  PRIVATE_SINGAPORE_DOMAIN,
  PRIVATE_SINGAPORE_VIA_ENV,
  APP_NAME
} from '../../constants';
import { isValidDate, getAgeFromDob } from '../../utils/date';
import { isValidEmail } from '../../utils/email';
import { loadQuestions } from '../../services/data';
import { getLocalFromUrl, getLocalFromBrowser } from '../../utils/locale';
import { APP_LOCALES } from '../../i18n';
import { MessageType, StorageType } from '../../enums';

const calculateScore = (sportScores, answerScores) => {
  let sumDiff = 0;
  let countDiff = 0;
  let score = 0;

  // test level 1 and level 2 criteria
  for (let j = 0; j < sportScores.length; j++) {
    if (sportScores[j] !== '' && answerScores[j] !== '') {
      sumDiff += Math.abs(sportScores[j] - answerScores[j]);
      countDiff += 1;
    }
  }

  if (countDiff != 0) {
    score = (10 - sumDiff / countDiff) * 10;
  }

  return score;
};

interface ISize {
  label: string;
  value: number;
}
export default class DataStore {
  routerStore: RouterStore;

  @observable
  locale = 'nl-be';

  @persist
  @observable
  currentRouteIsValid: boolean = false;

  @observable
  sporters = [];

  @observable
  activeSporter;

  @persist
  @observable
  uid: string = '';

  @persist
  @observable
  dob: string = '';

  @persist
  @observable
  firstname: string = '';

  @persist
  @observable
  surname: string = '';

  @persist
  @observable
  age: number;

  @persist
  @observable
  birthday: number;

  @persist
  @observable
  birthdayMonth: number;

  @persist
  @observable
  gender: string = '';

  @persist('object')
  @observable
  size: ISize;

  @persist('object')
  @observable
  _questionValues = {};

  @persist
  @observable
  activeQuestionValue: number;

  @observable
  questionIndex: number = 0;

  _questions = null;

  sports = null;

  @persist
  email: string = '';

  @persist
  optin: boolean = false;

  @persist
  country: string = '';

  @persist
  postalCode: string = '';

  @persist
  reportPdfUrl: string = '';

  isStandAlone = true;

  isSingaporePrivate =
    window.location.hostname.indexOf(PRIVATE_SINGAPORE_DOMAIN) !== -1 ||
    PRIVATE_SINGAPORE_VIA_ENV === 'true';

  isSportVlaanderen = APP_NAME === 'sportkompas-ilike';

  isHylyght = APP_NAME === 'hylyght-ilike';

  constructor(routerStore: RouterStore) {
    this.routerStore = routerStore;
    window.addEventListener('message', this.receiveData, false);

    // for slow browser
    const message = {
      type: MessageType.IFRAME_READY
    };
    window.parent.postMessage(JSON.stringify(message), '*');
  }

  @action
  redirectToLatestSession() {
    const path = sessionStorage.getItem(StorageType.ACTIVE_SESSION_PATH);
    if (path) {
      this.routerStore.push(path);
    }
  }

  @action
  async setLocale() {
    this.locale = getLocalFromUrl() || getLocalFromBrowser();

    if (this.isSingaporePrivate) {
      this.locale = APP_LOCALES.EN_SG;
    }

    await this.setQuestions(this.locale);
  }

  @action
  async setQuestions(locale) {
    const data = await loadQuestions(locale);
    const { questions, sports } = data;
    this._questions = questions;
    this.sports = sports;
  }

  // Called sometime after postMessage is called
  receiveData = event => {
    let message;

    try {
      message = JSON.parse(event.data);
    } catch (er) {
      //   console.log('error receiveData', er.toString());
    }

    if (message && message.type === MessageType.INIT_DATA) {
      this.isStandAlone = false;

      const { sporters } = message.data;
      this.setSporters(sporters);

      console.log('APP_NAME', APP_NAME);

      console.log('isSportVlaanderen', this.isSportVlaanderen);
      console.log('isStandAloneVersion', this.isStandAloneVersion);
      console.log('message', message);
      console.log('message.data', message.data);
    } else if (
      message &&
      message.type === MessageType.TEST_RECEIVED_SESSION_ID
    ) {
      sessionStorage.setItem(
        StorageType.ACTIVE_SESSION_ID,
        message.data.activeTestSessionId
      );
    }
  };

  @action
  setSporters(sporters) {
    this.sporters = sporters;
  }

  getScore() {
    if (this.activeSporter) {
      this.activeSporter.finished = true;
    }
    const result = [];
    const resultScores = [];

    if (this.sports.length > 0 && this.questions.length > 0) {
      this.sports.forEach(sport => {
        const { id, name, src, exclusive } = sport;

        const sportScores = this._questions.map(question => {
          const sportQuestionWeight = question.sport_weights.find(
            sw => sw.sport_id === id
          );
          const { weight } = sportQuestionWeight;
          resultScores.push(this._questionValues[question.id]);
          return weight;
        });

        const sportScore = calculateScore(sportScores, resultScores);

        result.push({
          id,
          name,
          exclusive,
          result: sportScore,
          src
        });
      });
    }
    result.sort((a, b) => b.result - a.result);
    // result.forEach(result => console.log(result.result, result.name));

    return result.filter(
      result => !(result.exclusive && result.exclusive !== this.gender)
    );
  }

  @action
  setCurrentRouteIsValid(value: boolean) {
    this.currentRouteIsValid = value;
  }

  @action
  startTest = (id: string) => {
    this.reset();
    if (this.sporters) {
      const sporter = this.sporters.find(sp => sp.id === id);
      if (sporter) {
        this.activeSporter = sporter;
        const age = getAgeFromDob(sporter.birthdate);
        this.setFirstname(sporter.firstname);
        this.setSurname(sporter.lastname);
        this.setGender(sporter.gender);
        this.setAge(age.toString());

        const message = {
          type: MessageType.TEST_STARTED,
          data: { sporter }
        };
        parent.postMessage(JSON.stringify(message), '*');

        this.routerStore.push('/');
      }
    }
  };

  login = async () => {
    // const postData = { birthdate: '1983-02-09', uid: 'AZERTY' };
    const postData = { uid: this.uid };
    // console.log('login', this.uid, this.dob);

    return fetch(PUBLIC_LOGIN_URL, {
      method: 'post',
      headers: {
        'Content-Type': 'application/json; charset=utf-8'
      },
      body: JSON.stringify(postData)
    });
  };

  saveData = () => {
    const score = this.getScore();
    const data = {
      result: toJS(score), // TODO: is dit nog nodig voor andere ILike tests?
      data: toJS(this._questionValues),
      status: 'finished'
    };

    if (this.isStandAloneVersion) {
      const postData = {
        ...data,
        firstname: this.firstname || this.uid,
        lastname: this.surname || this.dob,
        age: this.age,
        gender: this.gender,
        locale: this.locale,
        type: this.isSingaporePrivate ? 'nysiprivate' : 'ilike'
      };
      fetch(PUBLIC_SAVE_URL, {
        method: 'post',
        headers: {
          'Content-Type': 'application/json; charset=utf-8'
        },
        body: JSON.stringify(postData)
      })
        .then(response => response.text())
        .then(result => {
          const resultData = JSON.parse(result);

          if (resultData.report) {
            this.setReportPdfUrl(JSON.parse(result).report);
          }
        })
        .catch(error => console.error('Error:', error));
    } else {
      const message = {
        type: MessageType.TEST_FINISHED,
        data
      };
      parent.postMessage(JSON.stringify(message), '*');
      sessionStorage.removeItem(StorageType.ACTIVE_SESSION_ID);
    }
  };

  @action
  setReportPdfUrl(response) {
    this.reportPdfUrl = response;
  }

  pollerIsValidUrl = (target, times, delay) =>
    new Promise((res, rej) => {
      (function rec(i) {
        fetch(target).then(response => {
          if (response.status === 200) {
            res(true);
          } else {
            if (times === 0) return res(false);
            setTimeout(() => rec(--times), delay);
          }
        });
      })();
    });

  pollIsValidUrl = async (url, times = 3, delay = 1000) => {
    let response;
    try {
      response = await this.pollerIsValidUrl(url, times, delay);
    } catch (er) {}

    if (response) {
      return true;
    }
    return false;
  };

  openPdfInNewTab = async win => {
    const openWindow = win;

    if (this.isStandAloneVersion && this.reportPdfUrl !== '') {
      const pollResult = await this.pollIsValidUrl(this.reportPdfUrl, 15, 1000);

      // try catch needed for some case: example firefox mobile crashes on .focus()
      if (pollResult) {
        try {
          openWindow.location = this.reportPdfUrl;
          openWindow.focus();
        } catch (er) {}
      }
    }
  };

  @action
  setFirstname(firstname: string) {
    this.firstname = firstname;
    this.setCurrentRouteIsValid(this.nameValidator());
  }

  @action
  setSurname(surname: string) {
    this.surname = surname;
    this.setCurrentRouteIsValid(this.nameValidator());
  }

  @action
  setActiveQuestion(id) {
    if (this._questionValues[id] === undefined) {
      this.activeQuestionValue = -1;
    } else {
      this.activeQuestionValue = this._questionValues[id];
    }

    this.questionIndex = this.questions.findIndex(
      question => question.id === id
    );
  }

  @computed
  get questionProgress() {
    return `${this.questionIndex + 1}/${this.questions.length}`;
  }

  @computed
  get questions() {
    return this._questions
      ? this._questions.filter(question => question.type)
      : [];
  }

  @computed
  get isPrivate() {
    return this.sporters.length > 0;
  }

  // Check if the app is running from an iFrame
  @computed
  get isStandAloneVersion() {
    return this.isStandAlone;
  }

  @action
  setActiveQuestionValue(id, value) {
    this._questionValues[id] = value;
    this.activeQuestionValue = value;
    this.setCurrentRouteIsValid(this.questionValidator(id));
  }

  @action
  setGender(gender: string) {
    this.gender = gender;
    this.setCurrentRouteIsValid(this.genderValidator());
  }

  @action
  setAge(age: string) {
    this.age = Number(age);
    this.setCurrentRouteIsValid(this.ageValidator());
  }

  @action
  setUID(uid: string) {
    this.uid = uid;
  }

  @action
  setDOB(dob: string) {
    this.dob = dob;
  }

  @action
  setBirthday(birthday: string) {
    this.birthday = Number(birthday);
    this.setCurrentRouteIsValid(this.birthdayValidator());
  }

  @action
  setBirthdayMonth(month: number) {
    this.birthdayMonth = month;
    this.setCurrentRouteIsValid(this.birthdayValidator());
  }

  @action
  setSize(size: ISize) {
    this.size = size;
    this._questionValues['19'] = size.value;
    this.setCurrentRouteIsValid(this.sizeValidator());
  }

  @action
  setEmail(email: string) {
    this.email = email;
    this.setCurrentRouteIsValid(this.emailOptinValidator());
  }

  @action
  toggleOptin() {
    this.optin = !this.optin;
    this.setCurrentRouteIsValid(this.emailOptinValidator());
  }

  @action
  setCountry(country: string) {
    this.country = country;
    this.setCurrentRouteIsValid(this.addressValidator());
  }

  @action
  setPostalCode(postalCode: string) {
    this.postalCode = postalCode;
    this.setCurrentRouteIsValid(this.addressValidator());
  }

  nameValidator() {
    // return this.firstname.length > 0 && this.surname.length > 0;
    return this.firstname.length > 0;
  }

  questionValidator(id) {
    return (
      this._questionValues[id] !== undefined && this._questionValues[id] !== -1
    );
  }

  genderValidator() {
    return GENDER_TYPES.find(g => g === this.gender) !== undefined;
  }

  ageValidator() {
    return (
      this.age !== undefined &&
      !isNaN(this.age) &&
      this.age > 0 &&
      this.age < 120
    );
  }

  birthdayValidator() {
    return (
      this.birthday !== undefined &&
      !isNaN(this.birthday) &&
      this.birthday > 0 &&
      this.birthday <= 31 &&
      this.birthdayMonth !== undefined &&
      !isNaN(this.birthdayMonth) &&
      isValidDate(
        this.birthday,
        this.birthdayMonth,
        new Date().getFullYear() - this.age
      )
    );
  }

  sizeValidator() {
    return this.size
      ? SizeTypes.find(g => g.label === this.size.label) !== undefined
      : undefined;
  }

  emailOptinValidator() {
    return (
      this.email.length > 0 && isValidEmail(this.email) && this.optin === true
    );
  }

  addressValidator() {
    return this.country.length > 0 && this.postalCode.length > 0;
  }

  reset = () => {
    // user
    this.firstname = '';
    this.surname = '';
    this.dob = '';
    this.uid = '';
    this.age = undefined;
    this.birthday = undefined;
    this.birthdayMonth = undefined;
    this.gender = '';
    this.email = '';
    this.optin = false;
    this.country = '';
    this.postalCode = '';
    this.reportPdfUrl = '';

    // test
    this.size = undefined;
    this._questionValues = {};
    this.activeQuestionValue = undefined;
  };
}



// WEBPACK FOOTER //
// ./src/containers/store/DataStore.ts